import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, NavLink, useLocation } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import AppStatic, { isStudentsBuild, isTeachersBuild, isDemoBuild, isLandingBuild, isAdminBuild } from './config/AppStatic';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext'; // Import SnackbarProvider
import SnackbarContainer from './components/ui/SnackbarContainer'; // Import SnackbarContainer
import { generateUniqueCodeFromName } from './utils/helpers.ts';
import { useParams } from 'react-router-dom';
import DebugSection from './components/DebugSection';
import { InstitutionalSchedulePage } from './components/InstitutionalSchedulePage';
import SchoolHeader from './components/SchoolHeader';
import AssetManager from './services/AssetManager.ts';
import MaintenanceScreen from './components/MaintenanceScreen';
import MaintenanceBanner from './components/MaintenanceBanner';
import configService from './services/configService';
import { DocumentationPage } from './components/Documentation';
import UnsubscribePage from './components/UnsubscribePage';
import UltraAdmin from './components/UltraAdmin';
import InvitationAcceptPage from './components/InvitationAcceptPage';
import VersionChangeModal from './components/VersionChangeModal';
import VersionManager from './services/VersionManager';
import logger from './utils/logger';
import logo from './assets/logo.svg';
import AdminLandingPage from './components/AdminLandingPage';
import DemoOnboardingPage from './components/DemoOnboardingPage';
import './App.css';
import { DeviceLayoutProvider, useDeviceLayout } from './contexts/DeviceLayoutContext';

import { isDemoLoggedIn, clearDemoLogin } from './services/authService';
import { resetMockData } from './services/mockApiService';

// Import API service functions
import {
    initializeApiService,
    getTeachers, addTeacher, updateTeacher, deleteTeacher,
    getGroups, addGroup, updateGroup, deleteGroup,
    addStudentToGroup,
    addStudentsToGroupBatch,
    removeStudentFromGroup, updateGroupPreferences,
    getGroupById,
    getStudents, addStudent,
    getTeacherById, updateTeacherPreferences, updateTeacherQualifications,
    addRoom, updateRoom, deleteRoom, getRoomById,
    createScheduleEntry
} from './services/apiService';

import LoginScreen from './components/LoginScreen';
import ScheduleDisplay from './components/ScheduleDisplay';
import Button from './components/ui/Button';
import SetupPage from './components/SetupPage';
import WelcomePage from './components/WelcomePage.jsx';
import { clearLastInstitutionUrlEndpoint } from './utils/lastInstitutionUrlEndpoint';
import { recordNavigation } from './utils/appNavigationHistory';

// --- Route Guards (moved to module scope to avoid remounts) ---
function ProtectedRoute({ children }) {
    const { user, loading: authLoading } = useAuth();
    const [noServerEnabled, setNoServerEnabled] = useState(false);
    const [noServerChecked, setNoServerChecked] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const checkNoServer = async () => {
            try {
                const flag = await configService.isNoServerEnabled();
                if (isMounted) {
                    setNoServerEnabled(flag);
                    setNoServerChecked(true);
                }
            } catch (e) {
                if (isMounted) {
                    setNoServerEnabled(false);
                    setNoServerChecked(true);
                }
            }
        };
        checkNoServer();
        return () => {
            isMounted = false;
        };
    }, []);

    const demoSession = noServerEnabled && (isDemoLoggedIn() || isDemoBuild());
    const demoRole = AppStatic.getBuildType();
    const demoUser = demoSession ? {
        uid: null,
        email: 'demo@pi-schedule.local',
        role: demoRole === 'TEACHERS' ? 'Teacher' : demoRole === 'ADMIN' ? 'Admin' : 'Student',
        token: null
    } : null;
    const effectiveUser = user || demoUser;

    if (authLoading || !noServerChecked) {
        return <div className="p-6 text-center">Checking authentication...</div>;
    }

    // For paths OTHER than the new institutional schedule path,
    // if user is not authenticated, redirect to login.
    // The institutional path /:institutionUrlEndpoint/schedule has its own logic.
    if (!effectiveUser && !window.location.pathname.includes("/schedule")) {
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        if (pathParts.length === 2 && pathParts[1] === 'schedule') {
            // This is /:institution/schedule, let InstitutionalSchedulePage handle it
        } else if (window.location.pathname !== '/schedule' && window.location.pathname !== '/') { // Original /schedule and root path have their own logic
            return <Navigate to="/pi-admin" replace />;
        }
    }

    // Anonymous access to the main /schedule path is only valid for non-admin builds.
    if (!effectiveUser && window.location.pathname === '/schedule' && isAdminBuild()) {
        return <Navigate to="/pi-admin" replace />;
    }

    if (!effectiveUser && window.location.pathname === '/schedule') {
        const anonymousUser = { uid: null, email: null, role: 'Student', token: null };
        return React.cloneElement(children, { user: demoUser || anonymousUser });
    }

    return React.cloneElement(children, { user: effectiveUser || user });
}

function AdminRoute({ children }) {
    const { user, loading: authLoading } = useAuth();
    const [noServerEnabled, setNoServerEnabled] = useState(false);
    const [noServerChecked, setNoServerChecked] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const checkNoServer = async () => {
            try {
                const flag = await configService.isNoServerEnabled();
                if (isMounted) {
                    setNoServerEnabled(flag);
                    setNoServerChecked(true);
                }
            } catch (e) {
                if (isMounted) {
                    setNoServerEnabled(false);
                    setNoServerChecked(true);
                }
            }
        };
        checkNoServer();
        return () => {
            isMounted = false;
        };
    }, []);

    const demoSession = noServerEnabled && (isDemoLoggedIn() || isDemoBuild());
    const demoRole = AppStatic.getBuildType();
    const demoUser = demoSession ? {
        uid: null,
        email: 'demo@pi-schedule.local',
        role: demoRole === 'TEACHERS' ? 'Teacher' : demoRole === 'ADMIN' ? 'Admin' : 'Student',
        token: null
    } : null;
    const effectiveUser = user || demoUser;

    if (authLoading || !noServerChecked) {
        return <div className="p-6 text-center">Checking authorization...</div>;
    }
    if (!effectiveUser) {
        return <Navigate to="/" replace />;
    }
    if ((demoSession && demoUser?.role !== 'Admin') || (!demoSession && effectiveUser.role !== 'Admin')) {
        console.warn("Admin access denied for user:", effectiveUser?.email, "Role:", effectiveUser?.role);
        return <Navigate to="/schedule" replace />;
    }
    return React.cloneElement(children, { user: effectiveUser });
}

// Helper component to extract params for apiService initialization
function ApiServiceInitializer() {
    const { token, refreshToken, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const demoMode = isDemoBuild();
    const demoRole = AppStatic.getBuildType();
    const demoMappedRole = demoRole === 'TEACHERS' ? 'Teacher' : demoRole === 'ADMIN' ? 'Admin' : 'Student';
    const effectiveRole = useMemo(() => (demoMode ? demoMappedRole : user?.role), [demoMode, demoMappedRole, user?.role]);

    useEffect(() => {
        initializeApiService(
            () => token,
            () => {
                const pathParts = location.pathname.split('/').filter(Boolean);
                // Match shared draft public route: /:institutionUrlEndpoint/d/:slug
                if (pathParts.length >= 1 &&
                    pathParts[0] !== 'pi-admin' &&
                    pathParts[0] !== 'setup' &&
                    pathParts[0] !== 'debug' &&
                    pathParts[0] !== 'schedule'
                ) {
                    if (pathParts[1] === 'd' && pathParts[2]) {
                        // Compose endpoint path as institution/d/:slug so apiService uses /public/institutions/{that}/...
                        return `${pathParts[0]}/drafts/${pathParts[2]}`;
                    }
                    return pathParts[0];
                }
                return null;
            },
            refreshToken,
            () => {
                if (location.pathname !== '/schedule') return;
                if (effectiveRole !== 'Admin') return;
                navigate('/pi-admin', { replace: true });
            }
        );
    }, [token, location.pathname, refreshToken, navigate, effectiveRole]);

    return null;
}

function NavigationHistoryRecorder() {
    const location = useLocation();
    useEffect(() => {
        recordNavigation(location.pathname, location.search);
    }, [location.pathname, location.search]);
    return null;
}

function AppContent() {
    const { user, loading: authLoading, signOut } = useAuth();
    const { isMobileLayout } = useDeviceLayout();
    const navigate = useNavigate();
    const location = useLocation();
    const assetManager = useMemo(() => AssetManager.getInstance(), []);

    const [teachers, setTeachers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [rooms, setRooms] = useState(() => {
        const peek = AssetManager.getInstance().peekCachedRoomsRaw();
        return peek !== null ? peek : [];
    });
    const [subjects, setSubjects] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [error, setError] = useState(null);
    // Remove old snackbar state
    // const [snackbar, setSnackbar] = useState({ message: '', type: '', visible: false });
    const [justSignedUp, setJustSignedUp] = useState(false);
    const [isServerDown, setIsServerDown] = useState(false);
    const [showMaintenanceBanner, setShowMaintenanceBanner] = useState(false);
    const [maintenanceBannerDismissed, setMaintenanceBannerDismissed] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [showVersionChangeModal, setShowVersionChangeModal] = useState(false);
    const [versionChanges, setVersionChanges] = useState([]);
    const [showUserOverlay, setShowUserOverlay] = useState(false);
    const [institutionSettings, setInstitutionSettings] = useState(null);
    const [demoViewRole, setDemoViewRole] = useState(AppStatic.getBuildType());

    // Keep demo banner selection in sync when role changes outside the header (e.g. landing page pick)
    useEffect(() => {
        if (!isDemoBuild()) return;
        setDemoViewRole(AppStatic.getBuildType());
    }, [location.pathname]);

    // Demo session flags derived from build role (can be students/teachers/admin)
    const demoMode = isDemoBuild();
    const demoRole = AppStatic.getBuildType();
    const demoAdmin = demoMode && AppStatic.isAdminBuild();
    const hideAdminChrome = location.pathname === '/onboarding';
    const showAdminHeader =
        !hideAdminChrome && ((user && user.uid && user.role === 'Admin') || demoAdmin);
    const demoMappedRole = demoRole === 'TEACHERS' ? 'Teacher' : demoRole === 'ADMIN' ? 'Admin' : 'Student';
    const effectiveRouteUser = useMemo(() => {
        if (!demoMode) return user;
        return {
            ...(user || {}),
            uid: user?.uid ?? null,
            email: user?.email || 'demo@pi-schedule.local',
            role: demoMappedRole,
            token: user?.token ?? null
        };
    }, [demoMode, user, demoMappedRole]);

    logger.debug('[AppContent] Re-rendering. isLoadingData:', isLoadingData, 'Error:', error, 'Groups count:', groups.length);

    // Check maintenance mode from runtime configuration
    useEffect(() => {
        const checkMaintenanceMode = async () => {
            try {
                const [isMaintenanceBannerMode, message] = await Promise.all([
                    configService.isMaintenanceBannerEnabled(),
                    configService.getMaintenanceMessage()
                ]);

                // Check if banner was previously dismissed
                const wasDismissed = localStorage.getItem('maintenanceBannerDismissed') === 'true';

                setMaintenanceMessage(message);

                if (isMaintenanceBannerMode && !wasDismissed && !maintenanceBannerDismissed) {
                    setShowMaintenanceBanner(true);
                } else {
                    setShowMaintenanceBanner(false);
                }
            } catch (error) {
                console.error('[AppContent] Failed to check maintenance banner:', error);
                // On error, assume no maintenance banner
                setShowMaintenanceBanner(false);
            }
        };

        checkMaintenanceMode();

        // Check every 30 seconds for configuration updates
        const interval = setInterval(checkMaintenanceMode, 30000);

        return () => clearInterval(interval);
    }, [maintenanceBannerDismissed]);

    // Check for version updates
    useEffect(() => {
        if (isDemoBuild() || isTeachersBuild() || isStudentsBuild()) {
            setShowVersionChangeModal(false);
            setVersionChanges([]);
            return;
        }

        const checkVersionUpdate = () => {
            try {
                if (VersionManager.shouldShowChangelog()) {
                    const changes = VersionManager.getChangesSinceLastVersion();
                    setVersionChanges(changes);
                    setShowVersionChangeModal(true);
                }
            } catch (error) {
                console.error('[AppContent] Failed to check version updates:', error);
            }
        };

        checkVersionUpdate();
    }, []);

    // Log state changes
    useEffect(() => {

    }, [groups]);

    useEffect(() => {
        logger.debug('[AppContent] isLoadingData state changed:', isLoadingData);
    }, [isLoadingData]);

    useEffect(() => {
        logger.debug('[AppContent] error state changed:', error);
    }, [error]);

    // Remove old snackbar timeout effect
    // useEffect(() => {
    //     if (snackbar.visible) {
    //         const timer = setTimeout(() => {
    //             setSnackbar({ message: '', type: '', visible: false });
    //         }, 3000);
    //         return () => clearTimeout(timer);
    //     }
    // }, [snackbar.visible]);

    // Remove old showSnackbar function
    // const showSnackbar = (message, type = 'success') => {
    //     setSnackbar({ message, type, visible: true });
    // };

    // Helper function to detect if error indicates server is down
    const isServerDownError = (error) => {
        if (!error) return false;
        const errorMessage = error.message?.toLowerCase() || '';
        const errorCode = error.code;

        // Check for common server down indicators
        return (
            errorMessage.includes('network error') ||
            errorMessage.includes('failed to fetch') ||
            errorMessage.includes('connection refused') ||
            errorMessage.includes('service unavailable') ||
            errorMessage.includes('internal server error') ||
            errorCode === 'NETWORK_ERROR' ||
            errorCode === 503 ||
            errorCode === 500 ||
            error.name === 'TypeError' && errorMessage.includes('fetch')
        );
    };

    // Function to retry connecting to server
    const handleRetryConnection = useCallback(() => {
        setIsServerDown(false);
        setError(null);
        // This will trigger the data fetching useEffect
        if (user?.token) {
            window.location.reload();
        }
    }, [user?.token]);

    // Function to dismiss maintenance banner
    const handleDismissMaintenanceBanner = useCallback(() => {
        setMaintenanceBannerDismissed(true);
        setShowMaintenanceBanner(false);
        // Store dismissal in localStorage to persist across sessions
        localStorage.setItem('maintenanceBannerDismissed', 'true');
    }, []);

    // Function to handle version modal close
    const handleCloseVersionModal = useCallback(() => {
        setShowVersionChangeModal(false);
        // Mark current version as seen
        VersionManager.markCurrentVersionAsSeen();
    }, []);

    // Switch demo viewing role (students/teachers/admin) without leaving demo mode
    const handleSwitchDemoRole = useCallback((roleKey) => {
        const nextRole = AppStatic.setBuildRole(roleKey);
        setDemoViewRole(nextRole);
    }, []);

    // Fetch data when user changes or after signup/profile setup
    useEffect(() => {
        let isMounted = true;
        logger.debug('[AppContent] Main data fetching useEffect triggered. User:', user?.email, 'AuthLoading:', authLoading, 'JustSignedUp:', justSignedUp);

        // Skip data fetching for public routes that don't need this data
        const isPublicRoute = location.pathname.includes('/unsubscribe');
        if (isPublicRoute) {
            logger.debug('[AppContent-fetchData] Skipping fetch for public route:', location.pathname);
            return;
        }

        // Check if this is a public institutional page (/:institution) or shared-draft page (/:institution/d/:slug)
        const pathParts = location.pathname.split('/').filter(Boolean);
        const knownAppRoutes = ['pi-admin', 'schedule', 'setup', 'debug', 'documentation', 'institutions', 'onboarding'];
        const isInstitutionalPage = pathParts.length === 1 && !knownAppRoutes.includes(pathParts[0]);
        const isSharedDraftPage = pathParts.length >= 3 && !knownAppRoutes.includes(pathParts[0]) && pathParts[1] === 'd';

        const fetchData = async () => {
            const demoSession = isDemoBuild();
            const demoAdmin = demoSession && AppStatic.isAdminBuild();
            // For authenticated users, require token
            if (!demoAdmin && !demoSession) {
                if (user?.token || user?.email) {
                    if (!user?.token || authLoading) {
                        logger.debug('[AppContent-fetchData] Skipping fetch: no token, or auth loading.');
                        return;
                    }
                }
                // For public institutional or shared-draft pages, allow fetching without token
                else if (!isInstitutionalPage && !isSharedDraftPage) {
                    logger.debug('[AppContent-fetchData] Skipping fetch: no token and not an institutional page.');
                    return;
                }
            }
            // If we reach here, either we have a token OR we're on a public institutional page

            const fetchMode = demoSession
                ? `demo-${demoRole?.toLowerCase() || 'role'}`
                : (user?.token
                    ? 'authenticated'
                    : (isSharedDraftPage ? 'public-shared-draft' : 'public'));
            logger.debug(`[AppContent-fetchData] Starting data fetch in ${fetchMode} mode.`);
            setIsLoadingData(true);
            setError(null);

            try {
                const cachedRoomsRaw = assetManager.peekCachedRoomsRaw();
                if (cachedRoomsRaw !== null) {
                    setRooms(cachedRoomsRaw);
                }

                const [fetchedTeachers, fetchedGroups, roomsFetchResult, fetchedSubjects] = await Promise.all([
                    assetManager.fetchTeachers(),
                    assetManager.fetchGroups(),
                    assetManager.fetchRoomsRawStaleAware(),
                    assetManager.fetchSubjects()
                ]);

                if (!isMounted) return;

                setTeachers(fetchedTeachers || []);
                setGroups(fetchedGroups || []);
                if (cachedRoomsRaw === null) {
                    setRooms(roomsFetchResult.data || []);
                } else if (roomsFetchResult.changed) {
                    setRooms(roomsFetchResult.data || []);
                }
                setSubjects(fetchedSubjects || []);
            } catch (err) {
                if (!isMounted) return;
                console.error("Failed to fetch initial data:", err);

                // Check if this is a server downtime error
                if (isServerDownError(err)) {
                    logger.debug("Server appears to be down, showing maintenance screen");
                    setIsServerDown(true);
                    setError(null); // Clear regular error when showing maintenance screen
                } else {
                    let errorMessage = err.message || "An unknown error occurred";
                    if (err.code === 'PROFILE_SETUP_REQUIRED' || (err.message && err.message.includes('User profile setup required'))) {
                        errorMessage = "User profile setup is required. This should resolve shortly.";
                    }
                    setError(`Failed to load data: ${errorMessage}. Please try refreshing the page if this persists.`);
                    setIsServerDown(false);

                    const shouldForgetInstitution =
                        (isTeachersBuild() || isStudentsBuild()) &&
                        (isInstitutionalPage || isSharedDraftPage);
                    if (shouldForgetInstitution) {
                        clearLastInstitutionUrlEndpoint();
                        navigate('/', { replace: true });
                    }
                }
            } finally {
                if (isMounted) {
                    setIsLoadingData(false);
                    setJustSignedUp(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [user?.token, authLoading, assetManager, justSignedUp, location.pathname, demoRole, demoMode, navigate]);

    // Fetch institution settings for header logo
    useEffect(() => {
        const fetchInstitutionSettings = async () => {
            if (user?.token) {
                try {
                    const settings = await assetManager.fetchInstitutionSettings();
                    setInstitutionSettings(settings);
                } catch (err) {
                    console.error('Failed to fetch institution settings for header:', err);
                }
            }
        };
        fetchInstitutionSettings();
    }, [user?.token, assetManager]);

    // --- Logout Handler ---
    const handleLogout = async () => {
        try {
            // Also clear any demo login state
            clearDemoLogin();
            assetManager.invalidateScheduleEntriesCache();
            assetManager.invalidateRoomsCache();
            await signOut();
            navigate('/');
            logger.debug("User logged out");
        } catch (error) {
            console.error("Logout Error:", error);
            setError("Logout failed. Please try again.");
        }
    };

    // Close overlay when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showUserOverlay && !event.target.closest('.user-menu-container')) {
                setShowUserOverlay(false);
            }
        };

        if (showUserOverlay) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showUserOverlay]);

    // --- Room Handlers --- (update to use useSnackbar hook)
    const handleAddRoom = useCallback(async (newRoomInfo) => { // newRoomInfo will contain { name }
        if (!user || !user.token) { setError("Authentication error."); return; }
        setError(null); setIsLoadingData(true);

        const { name } = newRoomInfo; // Expecting only name from RoomsPage.jsx

        if (!name || !name.trim()) {
            setError("Room name is required.");
            setIsLoadingData(false);
            return;
        }

        // Generate unique room_code here
        const generatedRoomCode = generateUniqueCodeFromName(name, rooms, 'room_code', 'room');

        const payload = {
            name: name.trim(),
            room_code: generatedRoomCode,
            // capacity: newRoomInfo.capacity // If you add capacity later
        };
        logger.debug("Attempting to add room with payload:", payload); // Log payload

        try {
            // API service addRoom expects room_code and name
            const newlyCreatedRoom = await addRoom(payload, user.token);
            assetManager.invalidateRoomsCache();
            setRooms(prev => [...prev, newlyCreatedRoom]);
        } catch (err) {
            setError(`Failed to add room: ${err.message}`);
            let displayMessage = `Error adding room: ${err.message}`;
            if (err.message && err.message.toLowerCase().includes('unique constraint failed') && err.message.includes('room_code')) {
                displayMessage = `Error adding room: The generated room code '${generatedRoomCode}' might already exist or there was a conflict. Please try a slightly different name.`;
            }
            console.error("Error details for addRoom:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [user, rooms, assetManager]);

    const handleUpdateRoom = useCallback(async (roomDataToUpdate) => {
        if (!user || !user.token) { setError("Authentication error."); return; }
        setError(null); setIsLoadingData(true);
        const roomId = roomDataToUpdate.id;

        try {
            // API allows updating all room fields
            const payload = {
                name: roomDataToUpdate.name,
            };

            // Include optional fields if present
            if (roomDataToUpdate.hasOwnProperty('room_code')) {
                payload.room_code = roomDataToUpdate.room_code;
            }
            if (roomDataToUpdate.hasOwnProperty('maxCapacity')) {
                payload.max_capacity = roomDataToUpdate.maxCapacity;
            }
            if (roomDataToUpdate.hasOwnProperty('isHandicapAccessible')) {
                payload.is_handicap_accessible = roomDataToUpdate.isHandicapAccessible;
            }
            if (roomDataToUpdate.hasOwnProperty('equipment')) {
                payload.equipment = roomDataToUpdate.equipment;
            }
            if (roomDataToUpdate.hasOwnProperty('notes')) {
                payload.notes = roomDataToUpdate.notes;
            }
            if (roomDataToUpdate.hasOwnProperty('publicVisible')) {
                payload.public_visible = roomDataToUpdate.publicVisible;
            }
            if (roomDataToUpdate.hasOwnProperty('public_visible')) {
                payload.public_visible = roomDataToUpdate.public_visible;
            }

            const updatedRoomFromApi = await updateRoom(roomId, payload, user.token);
            assetManager.invalidateRoomsCache();
            setRooms(prevRooms => prevRooms.map(r => (r.id === roomId ? updatedRoomFromApi : r)));
            return updatedRoomFromApi;
        } catch (err) {
            setError(`Failed to update room ${roomDataToUpdate.name}: ${err.message}`);
            console.error("Error details for updateRoom:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [user, assetManager]);

    const handleDeleteRoom = useCallback(async (roomIdToDelete) => {
        if (!user || !user.token) { setError("Authentication error."); return; }
        setError(null); setIsLoadingData(true);
        let roomName = rooms.find(r => r.id === roomIdToDelete)?.name || 'the room';
        try {
            await deleteRoom(roomIdToDelete, user.token); // API returns 204 No Content on success
            assetManager.invalidateRoomsCache();
            setRooms(prev => prev.filter(r => r.id !== roomIdToDelete));
        } catch (err) {
            setError(`Failed to delete room: ${err.message}`);
            console.error("Error details for deleteRoom:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [user, rooms, assetManager]);

    // --- Teacher Handlers (Using AssetManager) ---
    const handleAddTeacher = useCallback(async (newTeacherInfo) => {
        setError(null);
        setIsLoadingData(true);
        try {
            const newTeacher = await assetManager.createTeacher(newTeacherInfo);
            setTeachers(prev => [...prev, newTeacher]);
        } catch (err) {
            setError(`Failed to add teacher: ${err.message}`);
            console.error("Error details for addTeacher:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [assetManager]);

    const handleUpdateTeacher = useCallback(async (teacherDataToUpdate) => {
        setError(null);
        setIsLoadingData(true);
        try {
            const updatedTeacher = await assetManager.updateTeacher(teacherDataToUpdate);
            setTeachers(prev => prev.map(t => t.id === updatedTeacher.id ? updatedTeacher : t));
            return updatedTeacher;
        } catch (err) {
            setError(`Failed to update teacher: ${err.message}`);
            console.error("Error details for updateTeacher:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [assetManager]);

    const handleDeleteTeacher = useCallback(async (teacherId) => {
        setError(null);
        setIsLoadingData(true);
        const teacherToDelete = teachers.find(t => t.id === teacherId);
        try {
            await assetManager.deleteTeacher(teacherId);
            setTeachers(prev => prev.filter(t => t.id !== teacherId));
            navigate('/setup/teachers', { replace: true });
        } catch (err) {
            setError(`Failed to delete teacher: ${err.message}`);
            console.error("Error details for deleteTeacher:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [assetManager, teachers, navigate]);

    // --- Subject Handlers (Using AssetManager) ---
    const handleAddSubject = useCallback(async (newSubjectInfo) => {
        setError(null);
        setIsLoadingData(true);
        try {
            const newSubject = await assetManager.createSubject(newSubjectInfo);
            setSubjects(prev => [...prev, newSubject]);
            return newSubject; // Return the new subject so it can be selected
        } catch (err) {
            setError(`Failed to add subject: ${err.message}`);
            console.error("Error details for addSubject:", err);
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [assetManager]);

    // --- Group Handlers (Using AssetManager) ---
    const handleAddGroup = useCallback(async (newGroupInfo) => {
        setError(null);
        setIsLoadingData(true);
        try {
            const newGroup = await assetManager.createGroup(newGroupInfo);
            setGroups(prev => [...prev, newGroup]);
            return newGroup; // Return the created group
        } catch (err) {
            setError(`Failed to add group: ${err.message}`);
            console.error("Error details for addGroup:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [assetManager]);

    const handleUpdateGroup = useCallback(async (groupDataToUpdate) => {
        setError(null);
        setIsLoadingData(true);
        try {
            // Use the new method that handles both core details and preferences
            const updatedGroup = await assetManager.updateGroupAndPreferences(groupDataToUpdate);
            setGroups(prev => prev.map(g => g.id === updatedGroup.id ? updatedGroup : g));
            return updatedGroup;
        } catch (err) {
            setError(`Failed to update group: ${err.message}`);
            console.error("Error details for updateGroup:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [assetManager]);

    const handleDeleteGroup = useCallback(async (groupIdToDelete) => {
        setError(null);
        setIsLoadingData(true);
        const groupToDelete = groups.find(g => g.id === groupIdToDelete);
        try {
            await assetManager.deleteGroup(groupIdToDelete);
            setGroups(prev => prev.filter(g => g.id !== groupIdToDelete));
            navigate('/setup/groups', { replace: true });
        } catch (err) {
            setError(`Failed to delete group: ${err.message}`);
            console.error("Error details for deleteGroup:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [assetManager, groups, navigate]);

    const handleAddStudentToGroup = useCallback(async (groupId, studentInfo) => {
        logger.debug(`[AppContent-handleAddStudentToGroup] Start. GroupID: ${groupId}`, studentInfo);
        if (!user || !user.token) {
            setError("Authentication error.");
            console.error("[handleAddStudentToGroup] Error: Missing user or token.");
            return;
        }
        setError(null);
        setIsLoadingData(true);
        let studentIdToAdd = null;

        try {
            // Try to create the student - if they exist, the API will return the existing student
            logger.debug(`[AppContent-handleAddStudentToGroup] Creating/getting student with email: ${studentInfo.email}`);
            const student = await addStudent(studentInfo);
            logger.debug(`[AppContent-handleAddStudentToGroup] Student response:`, student);

            if (!student || !student.id) {
                throw new Error("Failed to create/get student or received invalid response.");
            }

            studentIdToAdd = student.id;
            logger.debug(`[AppContent-handleAddStudentToGroup] Using student ID: ${studentIdToAdd}`);

            // Add the student to the group
            logger.debug(`[AppContent-handleAddStudentToGroup] Adding student ${studentIdToAdd} to group ${groupId}`);
            await addStudentToGroup(groupId, studentIdToAdd);
            logger.debug(`[AppContent-handleAddStudentToGroup] Student added to group API call complete.`);

            // Refresh the group data
            logger.debug(`[AppContent-handleAddStudentToGroup] Fetching updated group details for group ${groupId} (source: handleAddStudentToGroup)`);
            const updatedGroup = await getGroupById(groupId, user.token, 'students,preferences');
            logger.debug(`[AppContent-handleAddStudentToGroup] Updated group details received:`, updatedGroup);
            setGroups(prevGroups => {
                logger.debug(`[AppContent-handleAddStudentToGroup] Updating groups state.`);
                return prevGroups.map(g => g.id === groupId ? updatedGroup : g);
            });

            // Show success message
        } catch (err) {
            console.error("[AppContent-handleAddStudentToGroup] Error:", err);
            setError(`Failed to add student: ${err.message}`);
            console.error("Error details for addStudentToGroup:", err); // Log full error
            throw err;
        } finally {
            logger.debug(`[AppContent-handleAddStudentToGroup] Finalizing. Setting isLoadingData to false.`);
            setIsLoadingData(false);
        }
    }, [user]);

    const handleAddStudentsToGroupBatch = useCallback(async (groupId, students) => {
        logger.debug(`[AppContent-handleAddStudentsToGroupBatch] Start. GroupID: ${groupId}, Students count: ${students?.length}`);
        if (!user || !user.token) {
            setError("Authentication error.");
            console.error("[handleAddStudentsToGroupBatch] Error: Missing user or token.");
            return;
        }
        setError(null);
        setIsLoadingData(true);

        try {
            logger.debug(`[AppContent-handleAddStudentsToGroupBatch] Calling batch API for group ${groupId}`);
            const result = await addStudentsToGroupBatch(groupId, students);
            logger.debug(`[AppContent-handleAddStudentsToGroupBatch] Batch API response:`, result);

            // Refresh the group data
            logger.debug(`[AppContent-handleAddStudentsToGroupBatch] Fetching updated group details for group ${groupId}`);
            const updatedGroup = await getGroupById(groupId, user.token, 'students,preferences');
            logger.debug(`[AppContent-handleAddStudentsToGroupBatch] Updated group details received:`, updatedGroup);
            setGroups(prevGroups => {
                logger.debug(`[AppContent-handleAddStudentsToGroupBatch] Updating groups state.`);
                return prevGroups.map(g => g.id === groupId ? updatedGroup : g);
            });

            // Show success message
            const { successful, failed } = result.results;
        } catch (err) {
            console.error("[AppContent-handleAddStudentsToGroupBatch] Error:", err);
            setError(`Failed to add students: ${err.message}`);
            console.error("Error details for addStudentsToGroupBatch:", err); // Log full error
            throw err;
        } finally {
            logger.debug(`[AppContent-handleAddStudentsToGroupBatch] Finalizing. Setting isLoadingData to false.`);
            setIsLoadingData(false);
        }
    }, [user]);

    const handleRemoveStudentFromGroup = useCallback(async (groupId, studentId) => {
        logger.debug(`[AppContent-handleRemoveStudentFromGroup] Start. GroupID: ${groupId}, StudentID: ${studentId}`);
        if (!user || !user.token) { setError("Authentication error."); return; }
        setError(null); setIsLoadingData(true);
        let studentName = 'Unknown Student';
        try {
            const group = groups.find(g => g.id === groupId);
            const student = group?.students?.find(s => s.id === studentId);
            if (student) studentName = `${student.first_name} ${student.family_name}`;

            logger.debug(`[AppContent-handleRemoveStudentFromGroup] Removing student ${studentId} from group ${groupId}`);
            await removeStudentFromGroup(groupId, studentId, user.token);
            logger.debug(`[AppContent-handleRemoveStudentFromGroup] Student removed from group API call complete.`);

            logger.debug(`[AppContent-handleRemoveStudentFromGroup] Fetching updated group details for group ${groupId}`);
            // The original code for getGroupById in handleRemoveStudentFromGroup had user.token as the second arg, then includes.
            // Assuming your refactored getGroupById now consistently takes (id, includes, source) or similar.
            // Let's adjust to match the likely signature from the previous step, or what apiService expects.
            // If getGroupById still needs user.token explicitly, it should be passed.
            // For now, assuming 'students,preferences' is the 'includes' and the source is the third param.
            const updatedGroup = await getGroupById(groupId, 'students,preferences', 'handleRemoveStudentFromGroup');
            logger.debug(`[AppContent-handleRemoveStudentFromGroup] Updated group details received:`, updatedGroup);
            setGroups(prevGroups => {
                logger.debug(`[AppContent-handleRemoveStudentFromGroup] Updating groups state.`);
                return prevGroups.map(g => g.id === groupId ? updatedGroup : g);
            });
        } catch (err) {
            console.error("[AppContent-handleRemoveStudentFromGroup] Error:", err);
            setError(`Failed to remove student: ${err.message}`);
            console.error("Error details for removeStudentFromGroup:", err); // Log full error
            throw err;
        } finally {
            logger.debug(`[AppContent-handleRemoveStudentFromGroup] Finalizing. Setting isLoadingData to false.`);
            setIsLoadingData(false);
        }
    }, [user, groups]); // Added assetManager for consistency if getGroupById is involved

    // --- Schedule Entry Handlers ---
    const handleAddNewClass = useCallback(async (classData) => {
        if (!user || !user.token) {
            setError("Authentication error.");
            return;
        }
        setError(null);
        setIsLoadingData(true);

        try {
            logger.debug('[AppContent-handleAddNewClass] Creating new schedule entry:', classData);

            const scheduleEntryData = {
                type: 'class',
                subject: classData.subject || classData.subject_name || classData.course,
                ...(classData.teacher_id ? { teacher_id: classData.teacher_id } : {}),
                teacher: classData.teacher || classData.teacher_name || null,
                day: classData.day,
                weekSeries: classData.weekSeries,
                year: parseInt(classData.year, 10),
                room: classData.room || classData.room_name || null,
                timeStart: classData.timeStart || classData.start_time || null,
                timeEnd: classData.timeEnd || classData.end_time || null,
                groups: classData.groups,
            };

            logger.debug('[AppContent-handleAddNewClass] Transformed data for API:', scheduleEntryData);

            const newScheduleEntry = await createScheduleEntry(scheduleEntryData);
            logger.debug('[AppContent-handleAddNewClass] Created schedule entry:', newScheduleEntry);

        } catch (err) {
            console.error('[AppContent-handleAddNewClass] Error creating schedule entry:', err);
            setError(`Failed to add class: ${err.message}`);
            console.error("Error details for createScheduleEntry:", err); // Log full error
            throw err;
        } finally {
            setIsLoadingData(false);
        }
    }, [user]);

    // --- Route Components ---


    const getNavLinkClass = ({ isActive }) => {
        return `px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${isActive ? 'bg-gray-100 text-blue-600 font-semibold' : 'text-gray-700'}`;
    };

    // --- Render Loading State ---
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <p className="text-lg text-gray-600">Loading Application...</p>
            </div>
        );
    }

    // --- Render Maintenance Screen ---
    if (isServerDown) {
        return <MaintenanceScreen onRetry={handleRetryConnection} />;
    }

    // --- App Render ---
    return (
        <>
            <div className={`font-sans flex flex-col min-h-screen ${isMobileLayout ? 'mobile-layout' : ''}`}>
                {isDemoBuild() && (
                    <div className="bg-blue-600 text-white text-sm px-4 py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span className="font-semibold">
                            This is the Demo version of PI-Schedule.
                        </span>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs sm:text-sm font-medium">See the app as:</span>
                            {[
                                { key: 'STUDENTS', label: 'Student' },
                                { key: 'TEACHERS', label: 'Teacher' },
                                { key: 'ADMIN', label: 'Admin' }
                            ].map((role) => (
                                <button
                                    key={role.key}
                                    type="button"
                                    onClick={() => handleSwitchDemoRole(role.key)}
                                    className={`px-3 py-1 rounded-md border border-white/70 text-sm font-semibold transition ${demoViewRole === role.key ? 'bg-white text-blue-700 shadow-sm' : 'bg-transparent text-white hover:bg-white/15'}`}
                                >
                                    {role.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {showAdminHeader ? (
                    <header className="bg-white shadow-sm sticky top-0 z-40 flex-shrink-0 z-50">
                        <nav className="px-4 sm:px-6 lg:px-8">
                            <div className="flex justify-between h-16">
                                <div className="flex items-center">
                                    <Link to="/schedule" className="flex items-center flex-shrink-0">
                                        <div className="relative h-8 w-8 mr-2">
                                            <div className="shine-overlay"></div>
                                            <img src={logo} alt="Pi-Schedule Logo" className="h-8 w-8" />

                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xl font-bold text-blue-600" style={{ marginLeft: '-0.55rem', marginTop: '0.6rem', zIndex: 1000 }}>Schedule</span>
                                            <span className="text-gray-500" style={{ fontSize: '0.7rem', marginLeft: '-0.55rem', marginTop: '-0.5rem' }}>
                                                {AppStatic.getBuildType().toUpperCase()} {VersionManager.getCurrentVersion()}
                                            </span>
                                        </div>
                                    </Link>
                                    <div className="ml-6 flex space-x-4">
                                        <NavLink to="/schedule" className={getNavLinkClass}>Live Schedule</NavLink>
                                        <NavLink to="/setup" className={getNavLinkClass}>Setup</NavLink>
                                        <NavLink to="/documentation" className={getNavLinkClass}>Documentation</NavLink>
                                    </div>
                                </div>
                                <div className="flex items-center relative user-menu-container">
                                    {/* Circular institution logo */}
                                    <button
                                        onClick={() => setShowUserOverlay(!showUserOverlay)}
                                        className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors flex items-center justify-center overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        title="User menu"
                                    >
                                        {institutionSettings?.icon ? (
                                            <img
                                                src={institutionSettings.icon}
                                                alt={institutionSettings.name || 'Institution'}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<span class="text-gray-600 font-semibold text-lg">' + (institutionSettings?.name?.[0] || 'I') + '</span>';
                                                }}
                                            />
                                        ) : (
                                            <span className="text-gray-600 font-semibold text-lg">
                                                {institutionSettings?.name?.[0] || 'I'}
                                            </span>
                                        )}
                                    </button>

                                    {/* User overlay */}
                                    {showUserOverlay && (
                                        <div className="absolute right-0 top-12 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999]">
                                            {/* Institution name */}
                                            {institutionSettings?.name && (
                                                <div className="px-4 py-2 border-b border-gray-100">
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        {institutionSettings.name}
                                                    </p>
                                                </div>
                                            )}

                                            {/* User email / role (fallbacks for demo admin) */}
                                            <div className="px-4 py-2 border-b border-gray-100">
                                                <p className="text-sm text-gray-600">
                                                    {user?.email || (demoAdmin ? 'demo@pi-schedule.local' : '')}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {user?.role || (demoAdmin ? 'Admin' : '')}
                                                </p>
                                            </div>

                                            {/* Reset Mock Data button (demo only) */}
                                            {isDemoBuild() && (
                                                <div className="px-4 py-2 border-b border-gray-100">
                                                    <Button
                                                        onClick={async () => {
                                                            setShowUserOverlay(false);
                                                            try {
                                                                resetMockData();
                                                                // Reload the page to fetch fresh data from mock-data.json
                                                                window.location.reload();
                                                            } catch (error) {
                                                                console.error('Failed to reset mock data:', error);
                                                                setError('Failed to reset mock data. Please refresh the page manually.');
                                                            }
                                                        }}
                                                        variant="outline"
                                                        className="w-full text-sm py-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                                                    >
                                                        Reset Mock Data
                                                    </Button>
                                                </div>
                                            )}

                                            {/* Logout button */}
                                            <div className="px-4 py-2">
                                                <Button
                                                    onClick={() => {
                                                        setShowUserOverlay(false);
                                                        handleLogout();
                                                    }}
                                                    variant="secondary"
                                                    className="w-full text-sm py-2"
                                                >
                                                    Logout
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </nav>
                    </header>
                ) : (
                    < div />
                )}

                <main className="flex-grow">
                    {error && (
                        <div className="p-4 mb-4 bg-red-100 border border-red-400 text-red-700 rounded-md text-center" role="alert">
                            <p><strong>Error:</strong> {error}</p>
                            <button onClick={() => setError(null)} className="ml-4 text-red-700 font-bold">&times;</button>
                        </div>
                    )}

                    <Routes>
                        {isDemoBuild() && (isTeachersBuild() || isStudentsBuild()) && (
                            <Route path="/" element={<Navigate to="/schedule" replace />} />
                        )}
                        {(isTeachersBuild() || isStudentsBuild()) && !isDemoBuild() && (
                            <Route path="/" element={<WelcomePage />} />
                        )}
                        {/* Admin build root path */}
                        {!isTeachersBuild() && !isStudentsBuild() && (
                            <Route path="/" element={<Navigate to="/schedule" replace />} />
                        )}
                        <Route path="/pi-admin" element={!user?.uid ? <LoginScreen onSignupAndProfileSetupComplete={() => setJustSignedUp(true)} /> : <Navigate to="/schedule" replace />} />

                        <Route path="/onboarding" element={<DemoOnboardingPage />} />

                        <Route path="/schedule" element={
                            <ProtectedRoute>
                                <DndProvider backend={HTML5Backend}>
                                    <ScheduleDisplay
                                        user={effectiveRouteUser}
                                        teachers={teachers}
                                        groups={groups}
                                        subjects={subjects}
                                        rooms={rooms}
                                        isAssetsLoading={isLoadingData}
                                        onAddTeacher={handleAddTeacher}
                                        onAddSubject={handleAddSubject}
                                        onAddGroup={handleAddGroup}
                                    />
                                </DndProvider>
                            </ProtectedRoute>
                        } />

                        <Route path="/setup/*" element={
                            <ProtectedRoute>
                                <AdminRoute>
                                    <DndProvider backend={HTML5Backend}>
                                        <SetupPage
                                            user={effectiveRouteUser}
                                            teachers={teachers}
                                            onAddTeacher={handleAddTeacher}
                                            onUpdateTeacher={handleUpdateTeacher}
                                            onDeleteTeacher={handleDeleteTeacher}
                                            groups={groups}
                                            onAddGroup={handleAddGroup}
                                            onUpdateGroup={handleUpdateGroup}
                                            onDeleteGroup={handleDeleteGroup}
                                            onAddStudentToGroup={handleAddStudentToGroup}
                                            onAddStudentsToGroupBatch={handleAddStudentsToGroupBatch}
                                            onRemoveStudentFromGroup={handleRemoveStudentFromGroup}
                                            onAddNewClass={handleAddNewClass}
                                            rooms={rooms}
                                            onAddRoom={handleAddRoom}
                                            onUpdateRoom={handleUpdateRoom}
                                            onDeleteRoom={handleDeleteRoom}
                                            subjects={subjects}
                                        />
                                    </DndProvider>
                                </AdminRoute>
                            </ProtectedRoute>
                        } />

                        <Route path="/debug" element={
                            <ProtectedRoute>
                                <AdminRoute>
                                    <DebugSection />
                                </AdminRoute>
                            </ProtectedRoute>
                        } />

                        <Route path="/institutions/:institutionUrlEndpoint/students/:studentId/unsubscribe" element={
                            <UnsubscribePage />
                        } />

                        <Route path="/invite/accept" element={<InvitationAcceptPage />} />

                        <Route path="/ultra-admin" element={<UltraAdmin />} />

                        <Route path="/:institutionUrlEndpoint/d/:sharedDraftSlug" element={
                            <DndProvider backend={HTML5Backend}>
                                <InstitutionalSchedulePage
                                    user={effectiveRouteUser}
                                    teachers={teachers}
                                    groups={groups}
                                    subjects={subjects}
                                    rooms={rooms}
                                    isAssetsLoading={isLoadingData}
                                    onAddGroup={handleAddGroup}
                                />
                            </DndProvider>
                        } />

                        <Route path="/:institutionUrlEndpoint" element={
                            <DndProvider backend={HTML5Backend}>
                                <InstitutionalSchedulePage
                                    user={effectiveRouteUser}
                                    teachers={teachers}
                                    groups={groups}
                                    subjects={subjects}
                                    rooms={rooms}
                                    isAssetsLoading={isLoadingData}
                                    onAddGroup={handleAddGroup}
                                />
                            </DndProvider>
                        } />

                        <Route path="/documentation" element={
                            <ProtectedRoute>
                                <AdminRoute>
                                    <DocumentationPage />
                                </AdminRoute>
                            </ProtectedRoute>
                        } />

                        <Route path="/documentation/:category/:topic" element={
                            <ProtectedRoute>
                                <AdminRoute>
                                    <DocumentationPage />
                                </AdminRoute>
                            </ProtectedRoute>
                        } />

                        <Route path="/documentation/:category" element={
                            <ProtectedRoute>
                                <AdminRoute>
                                    <DocumentationPage />
                                </AdminRoute>
                            </ProtectedRoute>
                        } />

                        <Route
                            path="*"
                            element={
                                (isTeachersBuild() || isStudentsBuild())
                                    ? <Navigate to="/" replace />
                                    : <Navigate to={user ? "/schedule" : "/"} replace />
                            }
                        />
                    </Routes>
                </main>

                {/* Remove old Snackbar */}
                {/* {snackbar.visible && (
                    <div className={`fixed ${showMaintenanceBanner ? 'bottom-20' : 'bottom-4'} right-4 px-6 py-3 rounded-md shadow-lg z-50 ${snackbar.type === 'success' ? 'bg-green-500 text-white' : snackbar.type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'}`}>
                        {snackbar.message}
                    </div>
                )} */}

                {/* Add SnackbarContainer */}
                <SnackbarContainer />
            </div>

            {/* Maintenance Banner - Fixed at bottom */}
            {showMaintenanceBanner && (
                <MaintenanceBanner
                    onDismiss={handleDismissMaintenanceBanner}
                    message={maintenanceMessage}
                />
            )}

            {/* Version Change Modal - Only for admins on non-demo admin builds */}
            {!demoMode && user && user.role === 'Admin' && !isTeachersBuild() && !isStudentsBuild() && (
                <VersionChangeModal
                    isOpen={showVersionChangeModal}
                    onClose={handleCloseVersionModal}
                    changes={versionChanges}
                />
            )}
        </>
    );
}

function App() {
    return (
        <DeviceLayoutProvider>
            {isLandingBuild() ? (
                <AdminLandingPage />
            ) : (
                <AuthProvider>
                    <SnackbarProvider>
                        <NavigationHistoryRecorder />
                        <ApiServiceInitializer />
                        <AppContent />
                    </SnackbarProvider>
                </AuthProvider>
            )}
        </DeviceLayoutProvider>
    );
}

export default App;